import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';

export default function NotificationBell() {
  const {
    notifications,
    removeNotification,
    clearAllNotifications,
    soundEnabled,
    setSoundEnabled,
    notificationSettings,
    updateNotificationSettings,
    unreadCount
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    const icons = {
      milestone: '🎯',
      recovery: '📈',
      newToken: '🪙'
    };
    return icons[type] || '🔔';
  };

  const getNotificationColor = (type) => {
    const colors = {
      milestone: 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700',
      recovery: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700',
      newToken: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600';
  };

  const toggleNotificationType = (type) => {
    updateNotificationSettings({
      ...notificationSettings,
      [type]: !notificationSettings[type]
    });
  };

  const handleNotificationClick = (notification) => {
    setIsOpen(false);

    // Navigate based on notification type
    if (notification.type === 'milestone') {
      navigate('/milestones');
    } else if (notification.type === 'recovery') {
      navigate('/recovery');
    } else if (notification.type === 'newToken') {
      navigate('/tokens');
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
      >
        <span className="text-2xl">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-20 border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Notifications
                </h3>
                <div className="flex items-center gap-2">
                  {/* Settings Toggle */}
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    title="Notification settings"
                  >
                    ⚙️
                  </button>

                  {/* Sound Toggle */}
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
                  >
                    {soundEnabled ? '🔊' : '🔇'}
                  </button>

                  {/* Clear All */}
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAllNotifications}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Real-time alerts for milestones, recoveries, and new tokens
              </p>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Notification Types
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span>🎯</span>
                      Milestone Alerts
                    </span>
                    <input
                      type="checkbox"
                      checked={notificationSettings.milestones}
                      onChange={() => toggleNotificationType('milestones')}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span>📈</span>
                      Recovery Alerts
                    </span>
                    <input
                      type="checkbox"
                      checked={notificationSettings.recovery}
                      onChange={() => toggleNotificationType('recovery')}
                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span>🪙</span>
                      New Token Alerts
                    </span>
                    <input
                      type="checkbox"
                      checked={notificationSettings.newTokens}
                      onChange={() => toggleNotificationType('newTokens')}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Settings are saved automatically
                </p>
              </div>
            )}

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">🔕</div>
                  <p className="text-sm">No notifications yet</p>
                  <p className="text-xs mt-1">
                    You'll be notified when tokens hit milestones or start recovering
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 cursor-pointer ${getNotificationColor(notification.type)}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">
                              {getNotificationIcon(notification.type)}
                            </span>
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {notification.title}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                            {notification.message}
                          </p>

                          {/* Action buttons */}
                          <div className="flex items-center gap-2">
                            {notification.dexScreenerUrl && (
                              <a
                                href={notification.dexScreenerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                              >
                                📊 View Chart
                              </a>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                              className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              {notification.type === 'milestone' ? '🎯 View Milestones' :
                               notification.type === 'recovery' ? '📈 View Recovery' :
                               '🪙 View Tokens'}
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatTime(notification.timestamp)}
                            </span>
                          </div>
                        </div>

                        {/* Remove button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notification.id);
                          }}
                          className="ml-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-center">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Checking for updates every 30 seconds
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
